import i18next from 'i18next';
import { castArray, isNil } from 'lodash-es';

import { LS_KEY_LANGUAGE } from 'consts';
import { services } from 'services/base';
import { getMainConfig } from 'utils';

type LanguageState = {
  availableLanguages: string[];
  language: string;
};

const SERVICE_PREFIX = 'appFrame.language';
const ACTION_CHANGE_AVAILABLE_LANGUAGES = 'AppFrame:Language:ChangeAvailableLanguages';
const ACTION_CHANGE_LANGUAGE = 'AppFrame:Language:ChangeLanguage';

const initLanguageServices = () => {
  const { registerService, executeAction, registerAction, clearAction } = services;

  const availableLanguagesFromConfig = getMainConfig('languages') || [];
  const state: LanguageState = {
    availableLanguages: availableLanguagesFromConfig,
    language:
      services.appFrame.userStorage.getItem(LS_KEY_LANGUAGE) ||
      getMainConfig('defaultLanguage') ||
      availableLanguagesFromConfig[0] ||
      'en',
  };

  // available languages
  registerService(`${SERVICE_PREFIX}.getAvailableLanguages`, () => state.availableLanguages);
  registerAction(
    ACTION_CHANGE_AVAILABLE_LANGUAGES,
    (newLanguages: string | string[] | null) => {
      state.availableLanguages = isNil(newLanguages) ? [] : castArray(newLanguages);
    },
    { order: 0 },
  );
  registerService(`${SERVICE_PREFIX}.setAvailableLanguages`, (newLanguages: string | string[] | null) => {
    executeAction(ACTION_CHANGE_AVAILABLE_LANGUAGES, newLanguages);
  });
  registerService(`${SERVICE_PREFIX}.resetAvailableLanguages`, () => {
    executeAction(ACTION_CHANGE_AVAILABLE_LANGUAGES, availableLanguagesFromConfig);
  });
  registerService(
    `${SERVICE_PREFIX}.onAvailableLanguagesChange`,
    (callback: (newLanguages: string | string[] | null) => void) => {
      registerAction(ACTION_CHANGE_AVAILABLE_LANGUAGES, callback, { order: 1 });
      return () => clearAction(ACTION_CHANGE_AVAILABLE_LANGUAGES, callback);
    },
  );

  // selected language change
  registerService(`${SERVICE_PREFIX}.getLanguage`, () => state.language);
  registerAction(
    ACTION_CHANGE_LANGUAGE,
    (language: string) => {
      state.language = language;
      services.appFrame.userStorage.setItem(LS_KEY_LANGUAGE, language);
      i18next.changeLanguage(language);
    },
    { order: 0 },
  );
  registerService(`${SERVICE_PREFIX}.setLanguage`, (language: string) => {
    executeAction(ACTION_CHANGE_LANGUAGE, language);
  });
  registerService(`${SERVICE_PREFIX}.onLanguageChange`, (callback: (language: string) => void) => {
    registerAction(ACTION_CHANGE_LANGUAGE, callback, { order: 1 });
    return () => clearAction(ACTION_CHANGE_LANGUAGE, callback);
  });

  services.appFrame.userStorage.onUserStorageChange((newStorage) => {
    const language: string | null = newStorage.getItem(LS_KEY_LANGUAGE);
    if (!language) {
      return;
    }
    services.appFrame.language.setLanguage(language);
  });

  services.appFrame.language.setLanguage(state.language);
};

export default initLanguageServices;
